import _ from 'lodash';
import Ajv from 'ajv';
import ow from 'ow';
import { notBlankValidator, isValidEmail } from '../owValidators';
import { withTimeout } from '../lib';
import { DependencyError } from '../errors';

export class Identity {
  static localConnection = 'Username-Password-Authentication';

  static unknown(email) {
    return new Identity(email, false);
  }

  static knownLocal(email) {
    return new Identity(email, true);
  }

  static knownFederated(email, federatedConnection) {
    return new Identity(email, true, federatedConnection);
  }

  static fromIdentityPayload(email, connection) {
    if (_.isEqual(this.localConnection, connection)) {
      return this.knownLocal(email);
    }
    return this.knownFederated(email, connection);
  }

  constructor(email, isKnown, federatedConnection = null) {
    this.email = email;
    this.isKnown = isKnown;
    this.federatedConnection = federatedConnection;
    this.isFederated = !!federatedConnection;
  }
}

const toJsonStringOrPlainString = (maybeJson) => {
  try {
    return JSON.stringify(maybeJson);
  } catch (error) {
    return maybeJson;
  }
};

export class EmailFirstLookupError extends Error {
  constructor(message) {
    super(message);
    this.name = 'EmailFirstLookupError';
  }

  static fromUnexpectedResponse(status, text) {
    return new EmailFirstLookupError(
      `Unexpected response: status=${status}, content=${toJsonStringOrPlainString(
        text
      )}`
    );
  }
}

export class EmailFirstDependencyError extends DependencyError {
  constructor(message) {
    super(message);
    this.name = 'EmailFirstDependencyError';
  }

  static fromServerError(status, text) {
    return new EmailFirstLookupError(
      `Server Error: status=${status}, content=${toJsonStringOrPlainString(
        text
      )}`
    );
  }
}

class ResponseValidator {
  isIdentityStatus = (status) => _.eq(status, 200);
  isNotFoundStatus = (status) => _.eq(status, 404);
  isServerErrorStatus = (status) => _.gte(status, 500) && _.lte(status, 599);

  errorSchema = {
    type: 'object',
    required: ['error', 'error_description'],
    properties: {
      error: {
        type: 'string',
        enum: ['server_error'],
      },
      error_description: { type: 'string' },
    },
  };

  notFoundSchema = {
    type: 'object',
    required: ['error', 'error_description'],
    properties: {
      error: {
        type: 'string',
        enum: ['not_found'],
      },
      error_description: { type: 'string' },
    },
  };

  identitySchema = {
    type: 'object',
    required: ['email', 'identities'],
    properties: {
      email: { type: 'string' },
      identities: {
        type: 'array',
        minItems: 1,
        maxItems: 1,
        items: {
          type: 'object',
          minProperties: 1,
          maxProperties: 1,
          required: ['connection'],
          properties: {
            connection: { type: 'string' },
          },
        },
      },
    },
  };

  constructor() {
    this.validateIsServerError = new Ajv().compile(this.errorSchema);
    this.validateIsNotFound = new Ajv().compile(this.notFoundSchema);
    this.validateIsIdentity = new Ajv().compile(this.identitySchema);
  }

  isExpectedResponse(status, payload, statusValidator, schemaValidator) {
    try {
      return statusValidator(status) && schemaValidator(payload);
    } catch (error) {
      return false;
    }
  }

  isIdentityResponse(status, payload) {
    return this.isExpectedResponse(
      status,
      payload,
      this.isIdentityStatus,
      this.validateIsIdentity
    );
  }

  isNotFoundResponse(status, payload) {
    return this.isExpectedResponse(
      status,
      payload,
      this.isNotFoundStatus,
      this.validateIsNotFound
    );
  }
}

export class EmailFirst {
  constructor(lookupUrl) {
    ow(lookupUrl, 'lookupUrl', ow.string.validate(notBlankValidator));
    this.lookupUrl = lookupUrl;
    this.responseValidator = new ResponseValidator();
  }

  async lookup(email, txid, timeout = 5000) {
    ow(email, 'email', ow.string.validate(notBlankValidator));
    ow(email, 'email', ow.string.validate(isValidEmail));
    ow(txid, 'txid', ow.optional.string.validate(notBlankValidator));

    const identityUrl = `${this.lookupUrl}/${email}`;
    const headers = txid ? { 'X-Request-Id': txid } : {};
    const response = withTimeout(timeout, fetch(identityUrl, { headers }));

    return response.then(
      (res) => this.handleFulfilled({ givenEmail: email, response: res }),
      (err) => this.handleRejected(err)
    );
  }

  async handleFulfilled(context) {
    const givenEmail = context.givenEmail;
    const response = context.response;
    const status = response.status;

    const payload = await this.parseResponseContent(response);

    if (this.responseValidator.isServerErrorStatus(status)) {
      throw EmailFirstDependencyError.fromServerError(status, payload);
    }

    if (await this.responseValidator.isIdentityResponse(status, payload)) {
      return Identity.fromIdentityPayload(
        payload.email,
        payload.identities[0].connection
      );
    }

    if (await this.responseValidator.isNotFoundResponse(status, payload)) {
      return Identity.unknown(givenEmail);
    }

    throw EmailFirstLookupError.fromUnexpectedResponse(status, payload);
  }

  async handleRejected(error) {
    throw error;
  }

  async parseResponseContent(response) {
    let text = null;
    try {
      text = await response.text();
    } catch (textError) {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch (jsonError) {
      return text;
    }
  }
}
